import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { HardHat } from "lucide-react";
import HazardsTable from "@/components/risk-assessment/HazardsTable";
import PpeChecklist from "@/components/risk-assessment/PpeChecklist";
import EnvironmentSection from "@/components/risk-assessment/EnvironmentSection";
import VerdictSelector from "@/components/risk-assessment/VerdictSelector";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function RiskAssessmentDialog({ open, onOpenChange, inspectionId, assetCategory, existing, onSubmitted, readOnly = false }) {
  const [hazards, setHazards] = useState([]);
  const [ppe, setPpe] = useState([]);
  const [envState, setEnvState] = useState({ weather_ok: true, area_secured: true, permits_obtained: true, environmental_notes: "" });
  const [verdict, setVerdict] = useState("safe_to_proceed");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      if (existing) {
        setHazards((existing.hazards || []).map(h => ({ ...h, _key: h._key || h.id || uid() })));
        setPpe((existing.ppe || []).map(p => ({ ...p, _key: p._key || p.item || uid() })));
        setEnvState({
          weather_ok: !!existing.weather_ok,
          area_secured: !!existing.area_secured,
          permits_obtained: !!existing.permits_obtained,
          environmental_notes: existing.environmental_notes || "",
        });
        setVerdict(existing.overall_verdict || "safe_to_proceed");
        setNotes(existing.notes || "");
      } else {
        try {
          const { data } = await api.get("/risk-assessments/template", { params: { category: assetCategory || "generic" } });
          setHazards((data.hazards || []).map(h => ({ ...h, _key: uid() })));
          setPpe((data.ppe || []).map(p => ({ ...p, _key: p.item || uid() })));
          setEnvState({ weather_ok: true, area_secured: true, permits_obtained: true, environmental_notes: "" });
          setVerdict("safe_to_proceed"); setNotes("");
        } catch { toast.error("Failed to load template"); }
      }
    })();
  }, [open, existing, assetCategory]);

  const updateHazard = (idx, patch) => setHazards(hazards.map((h, i) => i === idx ? { ...h, ...patch } : h));
  const addHazard = () => setHazards([...hazards, { _key: uid(), hazard: "", likelihood: 1, severity: 1, controls: "", accepted: false }]);
  const removeHazard = (idx) => setHazards(hazards.filter((_, i) => i !== idx));
  const togglePpe = (idx, v) => setPpe(ppe.map((p, i) => i === idx ? { ...p, checked: v } : p));

  const maxScore = hazards.reduce((m, h) => Math.max(m, (h.likelihood || 0) * (h.severity || 0)), 0);
  const unmitigatedHigh = hazards.filter(h => (h.likelihood * h.severity) >= 15 && !h.accepted);
  const missingPpe = ppe.filter(p => !p.checked);

  const submit = async () => {
    if (hazards.some(h => !h.hazard.trim())) return toast.error("All hazards must have a description");
    setSaving(true);
    try {
      const { data } = await api.post("/risk-assessments", {
        inspection_id: inspectionId, hazards, ppe,
        weather_ok: envState.weather_ok, area_secured: envState.area_secured,
        permits_obtained: envState.permits_obtained, environmental_notes: envState.environmental_notes,
        overall_verdict: verdict, notes,
      });
      let msg;
      if (verdict === "stop") msg = "Risk assessment submitted — verdict: STOP";
      else if (verdict === "requires_mitigation") msg = "Risk assessment submitted — mitigation required";
      else msg = "Risk assessment approved — safe to proceed";
      toast[verdict === "stop" ? "error" : "success"](msg);
      onSubmitted?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="risk-assessment-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><HardHat className="w-5 h-5 text-[#D97706]" />Pre-Task Risk Assessment</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          <p className="text-xs text-gray-600 border-l-2 border-[#D97706] pl-3">
            Complete this assessment before starting the inspection. Any hazard with a risk score of 15+ (Likelihood × Severity) must be explicitly accepted with controls in place, or the verdict must be set to STOP.
          </p>

          <HazardsTable
            hazards={hazards}
            updateHazard={updateHazard}
            removeHazard={removeHazard}
            addHazard={addHazard}
            readOnly={readOnly}
            unmitigatedHigh={unmitigatedHigh}
          />

          <PpeChecklist
            ppe={ppe}
            togglePpe={togglePpe}
            readOnly={readOnly}
            missingCount={missingPpe.length}
          />

          <EnvironmentSection
            envState={envState}
            setEnvState={setEnvState}
            readOnly={readOnly}
          />

          <VerdictSelector
            verdict={verdict}
            setVerdict={setVerdict}
            readOnly={readOnly}
          />

          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Additional Notes</Label>
            <Textarea disabled={readOnly} data-testid="ra-notes" value={notes} onChange={e => setNotes(e.target.value)} className="rounded-md mt-2 text-sm min-h-[70px]" />
          </div>

          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 p-3">
            <div>Max risk score in this assessment: <b className="font-mono">{maxScore}</b></div>
            {existing?.submitted_at && <div className="mt-1">Last submitted: {new Date(existing.submitted_at).toLocaleString()} by <b>{existing.inspector_name}</b></div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && <Button data-testid="submit-ra-btn" onClick={submit} disabled={saving} className={`rounded-md ${verdict === "stop" ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#01ADEF] hover:bg-[#0198D6]"}`}>{saving ? "Saving…" : "Submit Assessment"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
